import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { SearchHit } from '@app/shared/schemas/search';
import { globalSearch } from '../../api/search';
import { useDebouncedValue } from '../../hooks/use-debounced-value';

/**
 * GlobalSearch (#search) — centrální vyhledávání v topbaru (⌘K).
 *
 * Controlled input + debounced dotaz → `GET /api/v1/search`. Výsledky zgrupované
 * do sekcí (Zakázky / Nabídky / Partneři), klávesnicová navigace (↑/↓, Enter,
 * Esc) přes zploštěný seznam, klik/Enter → proklik na detail. ⌘K / Ctrl+K
 * zafokusuje input z kteréhokoli místa. Zavírá se kliknutím mimo i po výběru.
 *
 * Admin + decision_maker only (endpoint je tak gatovaný); partner má portál bez
 * tohoto topbaru.
 */

type FlatHit = SearchHit & { kind: 'order' | 'offer' | 'partner' };

const MIN_QUERY_LEN = 2;
const SECTION_LABEL: Record<FlatHit['kind'], string> = {
  order: 'Zakázky',
  offer: 'Nabídky',
  partner: 'Partneři',
};

export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounced = useDebouncedValue(value.trim(), 250);
  const enabled = debounced.length >= MIN_QUERY_LEN;

  const query = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => globalSearch(debounced),
    enabled,
    staleTime: 30_000,
  });

  // Zploštění do jednoho seznamu pro klávesnicovou navigaci (pořadí = pořadí sekcí).
  const flat = useMemo<FlatHit[]>(() => {
    const data = query.data;
    if (!data) return [];
    return [
      ...data.orders.map((h) => ({ ...h, kind: 'order' as const })),
      ...data.offers.map((h) => ({ ...h, kind: 'offer' as const })),
      ...data.partners.map((h) => ({ ...h, kind: 'partner' as const })),
    ];
  }, [query.data]);

  // ⌘K / Ctrl+K fokus z kteréhokoli místa.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Zavřít při kliknutí mimo.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Reset zvýrazněné položky když se změní výsledky.
  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  const go = (hit: FlatHit): void => {
    setOpen(false);
    setValue('');
    if (hit.kind === 'order') void navigate({ to: '/orders/$id', params: { id: hit.id } });
    else if (hit.kind === 'offer') void navigate({ to: '/offers/$id', params: { id: hit.id } });
    else void navigate({ to: '/partners/$id', params: { id: hit.id } });
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open || flat.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const hit = flat[activeIndex];
      if (hit) go(hit);
    }
  };

  const showDropdown = open && enabled;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
          <svg
            className="fill-gray-500 dark:fill-gray-400"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
              fill=""
            />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKeyDown}
          placeholder="Hledat zakázku, nabídku, partnera…"
          aria-label="Centrální vyhledávání"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="global-search-results"
          className="dark:bg-dark-900 shadow-theme-xs h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 xl:w-[430px] dark:border-gray-800 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
        />
        <span
          className="absolute right-2.5 top-1/2 inline-flex -translate-y-1/2 items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 px-[7px] py-[4.5px] text-xs -tracking-[0.2px] text-gray-500 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400"
          aria-hidden
        >
          <span>⌘</span>
          <span>K</span>
        </span>
      </div>

      {showDropdown ? (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute left-0 right-0 z-50 mt-2 max-h-[60vh] overflow-auto rounded-xl border border-gray-200 bg-white py-2 shadow-theme-lg dark:border-gray-800 dark:bg-gray-900 xl:w-[430px]"
        >
          {query.isLoading ? (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">Hledám…</p>
          ) : query.error ? (
            <p className="px-4 py-3 text-sm text-error-600 dark:text-error-400">
              Vyhledávání selhalo.
            </p>
          ) : flat.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              Nic nenalezeno pro „{debounced}".
            </p>
          ) : (
            <SearchSections flat={flat} activeIndex={activeIndex} onPick={go} />
          )}
        </div>
      ) : null}
    </div>
  );
}

function SearchSections({
  flat,
  activeIndex,
  onPick,
}: {
  flat: FlatHit[];
  activeIndex: number;
  onPick: (hit: FlatHit) => void;
}) {
  // Render po sekcích, ale s globálním indexem (kvůli zvýraznění aktivní položky).
  const sections: FlatHit['kind'][] = ['order', 'offer', 'partner'];
  let runningIndex = -1;
  return (
    <>
      {sections.map((kind) => {
        const hits = flat.filter((h) => h.kind === kind);
        if (hits.length === 0) return null;
        return (
          <div key={kind} className="py-1">
            <div className="px-4 py-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {SECTION_LABEL[kind]}
            </div>
            {hits.map((hit) => {
              runningIndex += 1;
              const isActive = runningIndex === activeIndex;
              return (
                <button
                  key={`${hit.kind}-${hit.id}`}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onMouseDown={(e) => {
                    // mousedown (ne click) — předběhne blur/outside-close inputu.
                    e.preventDefault();
                    onPick(hit);
                  }}
                  className={`flex w-full items-baseline gap-2 px-4 py-2 text-left text-sm ${
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5'
                  }`}
                >
                  <span className="font-medium">{hit.label}</span>
                  {hit.sublabel ? (
                    <span className="truncate text-gray-500 dark:text-gray-400">
                      {hit.sublabel}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
